import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGarage, removeVehicle, toggleGarageLike } from '../store/slices/garageSlice';
import { toast } from 'react-toastify';
import api from '../services/api';
import { vehicleCoverThumb } from '../utils/media';

const CURRENT_YEAR = new Date().getFullYear();
const NHTSA = 'https://vpic.nhtsa.dot.gov/api/vehicles';

const POPULAR_MAKES = [
  'Acura','Alfa Romeo','Aston Martin','Audi','Bentley','BMW','Bugatti','Buick',
  'Cadillac','Chevrolet','Chrysler','Dodge','Ferrari','Fiat','Ford','Genesis',
  'GMC','Honda','Hyundai','Infiniti','Jaguar','Jeep','Kia','Lamborghini',
  'Land Rover','Lexus','Lincoln','Lotus','Maserati','Mazda','McLaren',
  'Mercedes-Benz','MINI','Mitsubishi','Nissan','Oldsmobile','Pagani','Plymouth',
  'Pontiac','Porsche','Ram','Rolls-Royce','Subaru','Suzuki','Tesla','Toyota',
  'Volkswagen','Volvo',
];

// Dropdown component for consistent searchable selects
const SearchableSelect = ({ label, value, options, onChange, placeholder, loading, disabled, allowCustom }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o =>
    (o.label || o).toLowerCase().includes(query.toLowerCase())
  ).slice(0, 50);

  const selectedLabel = options.find(o => (o.value ?? o) === value)?.label ?? value ?? '';

  const handleSelect = (opt) => {
    onChange(opt.value ?? opt, opt);
    setQuery('');
    setOpen(false);
  };

  const commitCustom = () => {
    if (allowCustom && query.trim()) {
      onChange(query.trim());
      setQuery('');
    }
    setOpen(false);
  };

  const exactMatch = filtered.some(o => (o.label ?? o).toLowerCase() === query.toLowerCase());
  const showCustomOption = allowCustom && open && query.trim() && !exactMatch;

  return (
    <div className="form-group" ref={ref} style={{ position: 'relative' }}>
      <label>{label} {loading && <span style={{ color: '#4a5170', fontSize: '0.75rem' }}>Loading...</span>}</label>
      <input
        className="input"
        value={open ? query : selectedLabel}
        disabled={disabled}
        placeholder={disabled ? '—' : placeholder}
        autoComplete="off"
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onBlur={commitCustom}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitCustom(); } }}
      />
      {open && !loading && (filtered.length > 0 || showCustomOption) && (
        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, background: '#12161f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, zIndex: 300, maxHeight: 220, overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
          {filtered.map((opt, i) => {
            const label2 = opt.label ?? opt;
            return (
              <div key={i} onMouseDown={() => handleSelect(opt)}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.875rem', color: '#c9ced6', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{label2}</div>
            );
          })}
          {showCustomOption && (
            <div onMouseDown={() => { onChange(query.trim()); setQuery(''); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.875rem', color: '#7a9fff', borderTop: filtered.length > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >Use &ldquo;{query.trim()}&rdquo;</div>
          )}
        </div>
      )}
    </div>
  );
};

const YEARS = Array.from({ length: CURRENT_YEAR - 1980 + 1 }, (_, i) => CURRENT_YEAR - i)
  .map(y => ({ value: String(y), label: String(y) }));

const fetchWithTimeout = (url, ms = 8000) => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(t));
};

const AddCarModal = ({ userId, onClose, onAdded }) => {
  const [step, setStep] = useState(1);

  const [makes, setMakes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(true);
  const [selectedMake, setSelectedMake] = useState('');
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [badge, setBadge] = useState('');
  const [vehicleType, setVehicleType] = useState('car');

  const [details, setDetails] = useState({
    nickname: '', color: '', odometer: '', condition: '',
    transmission: '', driveType: '', fuelType: '', notes: '',
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };
  const clearThumbnail = () => {
    setThumbnailFile(null);
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailPreview(null);
  };

  // Load all makes on mount
  useEffect(() => {
    fetchWithTimeout(`${NHTSA}/getallmakes?format=json`)
      .then(r => r.json())
      .then(data => {
        const allNames = (data.Results || []).map(m => m.Make_Name);
        if (allNames.length === 0) {
          setMakes(POPULAR_MAKES.map(m => ({ value: m, label: m })));
          return;
        }
        const popularSet = new Set(POPULAR_MAKES.map(m => m.toLowerCase()));
        const rest = allNames
          .filter(n => !popularSet.has(n.toLowerCase()))
          .sort((a, b) => a.localeCompare(b));
        const ordered = [...POPULAR_MAKES.filter(m => allNames.some(n => n.toLowerCase() === m.toLowerCase())), ...rest];
        setMakes(ordered.map(m => ({ value: m, label: m })));
      })
      .catch(() => setMakes(POPULAR_MAKES.map(m => ({ value: m, label: m }))))
      .finally(() => setMakesLoading(false));
  }, []);

  // Load models when make changes
  useEffect(() => {
    if (!selectedMake) { setModels([]); setSelectedModel(''); return; }
    setModelsLoading(true);
    setSelectedModel('');
    fetchWithTimeout(`${NHTSA}/getmodelsformake/${encodeURIComponent(selectedMake)}?format=json`)
      .then(r => r.json())
      .then(data => {
        const seen = new Set();
        const list = (data.Results || [])
          .filter(m => { if (seen.has(m.Model_Name)) return false; seen.add(m.Model_Name); return true; })
          .map(m => ({ value: m.Model_Name, label: m.Model_Name }));
        setModels(list);
      })
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [selectedMake]);

  const canProceed = selectedMake && selectedModel;
  const setD = (k, v) => setDetails(d => ({ ...d, [k]: v }));
  const summaryParts = [selectedYear, selectedMake, selectedModel, badge].filter(Boolean).join(' ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/garages/${userId}/vehicles`, {
        make: selectedMake,
        model: selectedModel,
        year: selectedYear ? parseInt(selectedYear) : null,
        badge: badge || null,
        vehicleType,
        nickname: details.nickname || null,
        color: details.color || null,
        odometer: details.odometer ? parseInt(details.odometer) : undefined,
        condition: details.condition || undefined,
        transmission: details.transmission || undefined,
        driveType: details.driveType || undefined,
        fuelType: details.fuelType || undefined,
        notes: details.notes || null,
      });
      let userVehicle = data.userVehicle;

      // Optional thumbnail upload. The server auto-sets the first image as
      // primary when no primary exists, so a single upload covers the
      // thumbnail and the rest of the gallery can be filled in later.
      if (thumbnailFile) {
        const fd = new FormData();
        fd.append('images', thumbnailFile);
        try {
          const { data: imgData } = await api.post(`/user-vehicles/${userVehicle.id}/images`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const primary = imgData.images?.find(i => i.isPrimary) || imgData.images?.[0];
          if (primary) userVehicle = { ...userVehicle, primaryImageUrl: primary.imageUrl, images: imgData.images };
        } catch {
          toast.error('Vehicle added, but the thumbnail failed to upload.');
        }
      }

      onAdded(userVehicle);
      toast.success(`${selectedMake} ${selectedModel} added to your garage.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, width: '90vw' }}>
        <div className="modal-header">
          <h3>{step === 1 ? 'Add a Vehicle' : summaryParts}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', padding: '0 24px', borderBottom: '1px solid #2a2f45' }}>
          {['Find Vehicle', 'Details'].map((label, i) => (
            <div key={label} style={{ flex: 1, padding: '12px 0', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: step === i + 1 ? '#c9ced6' : '#5a657a', borderBottom: step === i + 1 ? '2px solid #c9ced6' : '2px solid transparent', cursor: i === 0 ? 'pointer' : 'default' }}
              onClick={() => { if (i === 0) setStep(1); }}>
              <span style={{ marginRight: 6 }}>{i + 1}</span>{label}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="modal-body">
            {/* Vehicle type */}
            <div className="form-group">
              <label>Vehicle Type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['car', 'Car'], ['motorcycle', 'Motorcycle'], ['truck', 'Truck'], ['other', 'Other']].map(([val, lbl]) => (
                  <button key={val} type="button" onClick={() => setVehicleType(val)}
                    className={vehicleType === val ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}>{lbl}</button>
                ))}
              </div>
            </div>

            {/* Make */}
            <SearchableSelect
              label="Make"
              value={selectedMake}
              options={makes}
              onChange={(val) => { setSelectedMake(val); setSelectedModel(''); setSelectedYear(''); }}
              placeholder={makesLoading ? 'Loading makes…' : 'Search or type make…'}
              loading={makesLoading}
              allowCustom
            />

            {/* Model */}
            <SearchableSelect
              label="Model"
              value={selectedModel}
              options={models}
              onChange={(val) => setSelectedModel(val)}
              placeholder={modelsLoading ? 'Loading models…' : !selectedMake ? 'Select make first' : 'Search or type model…'}
              loading={modelsLoading}
              disabled={!selectedMake}
              allowCustom
            />

            {/* Year */}
            <SearchableSelect
              label={<>Year <span style={{ color: '#4a5170' }}>(optional)</span></>}
              value={selectedYear}
              options={YEARS}
              onChange={(val) => setSelectedYear(val)}
              placeholder="Select year…"
              disabled={!selectedModel}
            />

            {/* Badge — free text */}
            <div className="form-group">
              <label>Badge / Trim <span style={{ color: '#4a5170' }}>(optional)</span></label>
              <input className="input" value={badge} onChange={e => setBadge(e.target.value)} placeholder="e.g. GTR, Type R, M Sport, TRD…" />
            </div>

            {canProceed && (
              <div style={{ padding: '10px 14px', background: 'rgba(201,206,214,0.06)', border: '1px solid rgba(201,206,214,0.15)', borderRadius: 0, marginTop: 4, fontSize: '0.85rem', color: '#c9ced6' }}>
                <strong>Selected:</strong> {summaryParts}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Thumbnail photo <span style={{ color: '#4a5170' }}>(optional)</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label
                    htmlFor="add-vehicle-thumbnail"
                    style={{
                      width: 120, height: 90, borderRadius: 10,
                      border: '1px dashed rgba(255,255,255,0.20)',
                      background: thumbnailPreview ? `center/cover no-repeat url(${thumbnailPreview})` : 'rgba(255,255,255,0.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0, position: 'relative', overflow: 'hidden',
                    }}
                  >
                    {!thumbnailPreview && (
                      <span style={{ fontSize: '0.78rem', color: '#8892b0', textAlign: 'center', padding: '0 8px' }}>+ Add photo</span>
                    )}
                    {thumbnailPreview && (
                      <span style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', color: '#fff', opacity: 0, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                      >Change</span>
                    )}
                  </label>
                  <input id="add-vehicle-thumbnail" type="file" accept="image/*" onChange={handleThumbnailChange} style={{ display: 'none' }} />
                  <div style={{ flex: 1, fontSize: '0.78rem', color: '#8892b0', lineHeight: 1.5 }}>
                    Add a single thumbnail. You can upload the rest of the photos to the gallery after the vehicle is created.
                    {thumbnailFile && (
                      <button type="button" onClick={clearThumbnail} style={{ display: 'block', marginTop: 6, background: 'none', border: 'none', color: '#e84545', cursor: 'pointer', padding: 0, fontSize: '0.78rem' }}>Remove photo</button>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Nickname <span style={{ color: '#4a5170' }}>(optional)</span></label>
                  <input className="input" value={details.nickname} onChange={e => setD('nickname', e.target.value)} placeholder="My baby" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Colour <span style={{ color: '#4a5170' }}>(optional)</span></label>
                  <input className="input" value={details.color} onChange={e => setD('color', e.target.value)} placeholder="e.g. Midnight Blue" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Odometer (km)</label>
                  <input
                    className="input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={details.odometer}
                    onChange={e => setD('odometer', e.target.value.replace(/\D/g, ''))}
                    placeholder="45000"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Condition</label>
                  <select className="input" value={details.condition} onChange={e => setD('condition', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Transmission</label>
                  <select className="input" value={details.transmission} onChange={e => setD('transmission', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                    <option value="cvt">CVT</option>
                    <option value="dct">DCT</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Drive Type</label>
                  <select className="input" value={details.driveType} onChange={e => setD('driveType', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="fwd">FWD</option>
                    <option value="rwd">RWD</option>
                    <option value="awd">AWD</option>
                    <option value="4wd">4WD</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label>Fuel Type</label>
                  <select className="input" value={details.fuelType} onChange={e => setD('fuelType', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="electric">Electric</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 14, marginBottom: 0 }}>
                <label>Notes <span style={{ color: '#4a5170' }}>(optional)</span></label>
                <textarea className="input" rows={2} value={details.notes} onChange={e => setD('notes', e.target.value)} placeholder="Anything you want to note about this vehicle…" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add to Garage'}
              </button>
            </div>
          </form>
        )}

        {step === 1 && (
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={!canProceed} onClick={() => setStep(2)}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const GaragePage = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const { garage, vehicles, value, loading, liked } = useSelector(s => s.garage);
  const { user: me } = useSelector(s => s.auth);
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState('grid');

  const isOwner = String(me?.id) === String(userId);

  useEffect(() => { dispatch(fetchGarage(userId)); }, [userId, dispatch]);

  const handleRemove = async (userVehicleId) => {
    if (!window.confirm('Remove this vehicle from your garage?')) return;
    const result = await dispatch(removeVehicle({ userId, userVehicleId }));
    if (removeVehicle.fulfilled.match(result)) toast.success('Vehicle removed');
    else toast.error('Failed to remove');
  };

  const handleAdded = () => { dispatch(fetchGarage(userId)); };

  const handleToggleLike = () => {
    if (isOwner) return;
    dispatch(toggleGarageLike({ userId, liked }));
  };

  if (loading) return <div className="loading-page"><div className="loading-spinner" /></div>;

  const ownedVehicles = vehicles.filter(v => !v.isDreamBuild);

  return (
    <div className="garage-page">
      {/* Header */}
      <div className="garage-page-header">
        <div className="garage-page-title-block">
          <h1 className="garage-page-title">{garage?.name || 'Garage'}</h1>
          <p className="garage-page-meta">
            {ownedVehicles.length} {ownedVehicles.length === 1 ? 'vehicle' : 'vehicles'}
            <span className="garage-page-stat" title="Views">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              {garage?.viewCount || 0}
            </span>
            <span className="garage-page-stat" title="Likes">
              <svg width="14" height="14" viewBox="0 0 24 24" fill={liked ? '#ff5577' : 'none'} stroke={liked ? '#ff5577' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              {garage?.likeCount || 0}
            </span>
          </p>
        </div>
        <div className="garage-page-actions">
          {!isOwner && (
            <button
              className={`collection-like-btn${liked ? ' liked' : ''}`}
              onClick={handleToggleLike}
              aria-label={liked ? 'Unlike collection' : 'Like collection'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24"
                fill={liked ? '#ff5577' : 'none'}
                stroke={liked ? '#ff5577' : 'currentColor'}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span>{liked ? 'Liked' : 'Like'}</span>
            </button>
          )}
          <div className="posts-view-toggle">
            <button className={`posts-view-btn${view === 'grid' ? ' active' : ''}`} onClick={() => setView('grid')} aria-label="Grid view">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                <rect x="0" y="0" width="6.5" height="6.5" />
                <rect x="8.5" y="0" width="6.5" height="6.5" />
                <rect x="0" y="8.5" width="6.5" height="6.5" />
                <rect x="8.5" y="8.5" width="6.5" height="6.5" />
              </svg>
            </button>
            <button className={`posts-view-btn${view === '3d' ? ' active' : ''}`} onClick={() => setView('3d')} aria-label="3D view">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </button>
          </div>
          {isOwner && (
            <button onClick={() => setShowAdd(true)} className="btn-primary btn-sm">+ Add Vehicle</button>
          )}
        </div>
      </div>

      {/* 3D view stub */}
      {view === '3d' && (
        <div className="garage-3d-stub">
          <div className="garage-3d-stub-inner">
            <div className="garage-3d-stub-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h2 className="garage-3d-stub-title">3D Garage</h2>
            <p className="garage-3d-stub-sub">Interactive 3D view of your collection is coming soon. You'll be able to walk through your garage, inspect each car up close, and customise the space.</p>
            <div className="garage-3d-stub-tags">
              <span>Interactive walkthrough</span>
              <span>Car inspection mode</span>
              <span>Custom environments</span>
            </div>
          </div>
        </div>
      )}

      {/* Grid view */}
      {view === 'grid' && (
        <div className="container" style={{ background: 'none' }}>
          {ownedVehicles.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">—</div>
              <h3>Your garage is empty</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
                {isOwner ? 'Add your first vehicle to get started.' : 'This user has no vehicles yet.'}
              </p>
              {isOwner && <button className="btn-primary" onClick={() => setShowAdd(true)}>Add your first vehicle</button>}
            </div>
          ) : (
            <div className="garage-grid">
              {ownedVehicles.map((uv, i) => (
                <div key={uv.id} className="garage-card-wrap">
                  <Link to={`/vehicle/${uv.id}`} className="garage-card">
                    <div className="garage-card-img-wrap">
                      {vehicleCoverThumb(uv)
                        ? <img src={vehicleCoverThumb(uv)} alt="" className="garage-card-img" />
                        : <div className="garage-card-placeholder">
                            {uv.vehicle?.vehicleType === 'motorcycle' ? 'BIKE' : uv.vehicle?.vehicleType === 'truck' ? 'TRUCK' : 'CAR'}
                          </div>}
                    </div>
                    <div className="garage-card-info">
                      <div className="garage-card-num">0{i + 1}</div>
                      <div className="garage-card-name">
                        {uv.nickname || `${uv.vehicle?.make} ${uv.vehicle?.model}`}
                      </div>
                      <div className="garage-card-year">{uv.vehicle?.year}{uv.odometer ? ` · ${Number(uv.odometer).toLocaleString()} km` : ''}</div>
                    </div>
                  </Link>
                  {isOwner && (
                    <button className="garage-card-remove" onClick={() => handleRemove(uv.id)}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showAdd && <AddCarModal userId={userId} onClose={() => setShowAdd(false)} onAdded={handleAdded} />}
    </div>
  );
};

export default GaragePage;
